import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { getMapData, type MapId } from '../shared/mapData';

// Phase 138 — Map briefing overlay shown when a match loads. 4-second hold
// then fade. Pulls all copy from MAPS[mapId].briefing.

const briefingKeyframes = `
  @keyframes briefingFadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes briefingFadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
`;

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(180deg, rgba(4,8,16,0.92) 0%, rgba(4,8,16,0.78) 100%)',
  color: '#dceeff',
  zIndex: 220,
  pointerEvents: 'none',
};

export interface MapBriefingProps {
  mapId: MapId;
  onComplete?: () => void;
  holdMs?: number;
  fadeMs?: number;
}

export function MapBriefing({ mapId, onComplete, holdMs = 4000, fadeMs = 600 }: MapBriefingProps) {
  const map = getMapData(mapId);
  const briefing = map.briefing;
  const [fading, setFading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!briefing) {
      onComplete?.();
      setDone(true);
      return;
    }
    const fadeTimer = setTimeout(() => setFading(true), holdMs);
    const doneTimer = setTimeout(() => {
      setDone(true);
      onComplete?.();
    }, holdMs + fadeMs);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(doneTimer);
    };
  }, [briefing, holdMs, fadeMs, onComplete]);

  if (done || !briefing) return null;

  return (
    <div
      style={{
        ...overlayStyle,
        animation: fading
          ? `briefingFadeOut ${fadeMs}ms ease-out forwards`
          : `briefingFadeIn 500ms ease-out both`,
      }}
      data-testid="map-briefing"
    >
      <style>{briefingKeyframes}</style>
      <div style={{
        fontFamily: 'monospace',
        fontSize: 11,
        letterSpacing: 4,
        color: '#62ddff',
        textTransform: 'uppercase',
      }}>
        Map Loaded
      </div>
      <div style={{
        marginTop: 12,
        fontSize: 56,
        letterSpacing: 6,
        fontWeight: 700,
        textTransform: 'uppercase',
      }}>
        {map.label}
      </div>
      <div style={{
        marginTop: 18,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        fontFamily: 'monospace',
        fontSize: 16,
        letterSpacing: 2,
      }}>
        <span style={{ color: '#ff5555' }}>{briefing.factionLeft}</span>
        <span style={{ color: '#6688aa' }}>vs</span>
        <span style={{ color: '#62ddff' }}>{briefing.factionRight}</span>
      </div>
      <div style={{
        marginTop: 26,
        maxWidth: 540,
        textAlign: 'center',
        fontSize: 15,
        lineHeight: 1.55,
        color: '#aaccdd',
      }}>
        {briefing.blurb}
      </div>
      <div style={{
        marginTop: 28,
        padding: '10px 20px',
        border: '1px solid rgba(98,221,255,0.3)',
        borderRadius: 4,
        background: 'rgba(98,221,255,0.06)',
        fontFamily: 'monospace',
        fontSize: 12,
        letterSpacing: 2,
        color: '#62ddff',
        textTransform: 'uppercase',
      }}>
        Recommended: {briefing.recommendedLoadout}
      </div>
    </div>
  );
}
